import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { StackIcon } from '@phosphor-icons/react';
import { getRouteApi } from '@tanstack/react-router';
import type { MouseEvent } from 'react';
import { Fragment, useState } from 'react';

import { MapLayerIcon } from '@/components/display/MapLayerIcon';
import { useScreenSize } from '@/hooks/useScreenSize';
import { I18N_DE } from '@/i18n/de';
import { useMapStore } from '@/stores/useMapStore';
import type { MapLayer } from '@/types/MapLayer';
import { zMapLayer } from '@/types/MapLayer';
import { zMapStyle } from '@/types/MapStyle';

const route = getRouteApi('/_public/_map');

export function LayerControl() {
  const { isDesktop } = useScreenSize();

  const navigate = route.useNavigate();
  const { layers } = route.useSearch();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const style = useMapStore((state) => state.style);
  const { setStyle } = useMapStore((state) => state.actions);

  const toggleLayer = (layer: MapLayer) => {
    navigate({
      search: (prev) => ({
        layers: prev.layers.includes(layer) ? prev.layers.filter((l) => l !== layer) : [...(prev?.layers || []), layer],
      }),
    });
  };

  return (
    <Fragment>
      <IconButton
        aria-controls={open ? 'layer-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        id="layer-button"
        onClick={handleOpen}
        title="Ebenen"
        sx={{
          borderRadius: 0,
          width: 56,
          height: 56,
          backgroundColor: 'primary.main',
          color: 'common.white',

          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        }}
      >
        <StackIcon color="#fff0f0" size={24} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id="layer-menu"
        onClose={handleClose}
        open={open}
        anchorOrigin={{
          vertical: 'top',
          horizontal: isDesktop ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isDesktop ? 'left' : 'right',
        }}
      >
        <Box style={{ padding: '0 16px', marginBottom: 8 }}>
          <FormLabel>Karteninhalte</FormLabel>
        </Box>

        {zMapLayer.options.map((mapLayer) => (
          <MenuItem key={mapLayer} onClick={() => toggleLayer(mapLayer)} selected={layers?.includes(mapLayer)}>
            <Stack alignItems="center" direction="row" gap={1.5}>
              <MapLayerIcon mapLayer={mapLayer} />
              <Typography variant="body2">{I18N_DE.MapLayer[mapLayer]}</Typography>
            </Stack>
          </MenuItem>
        ))}
        <FormControl style={{ padding: '0 16px', marginTop: 8 }}>
          <FormLabel>Hintergrundkarte</FormLabel>
          <RadioGroup onChange={(event) => setStyle(zMapStyle.parse(event.target.value))} value={style}>
            <FormControlLabel control={<Radio />} label="Basis (grau)" value="BASIC" />
            <FormControlLabel control={<Radio />} label="Luftbild" value="SATELLITE" />
          </RadioGroup>
        </FormControl>
      </Menu>
    </Fragment>
  );
}
